import { createHttpClient, createStandardApiParser, type HttpError } from "@gunziai/http";
import {
    AuthDialogPage,
    notifyPowerBalanceChanged,
    openAuthDialog,
    useAuthStore,
} from "@gunziai/stores";
import type { AxiosResponse } from "axios";
import { toast } from "sonner";

import { getFirstConsoleMenuPath, hasConsoleAccess, WEB_HOME_PATH } from "./shared/console-access";

const isDev = import.meta.env.DEV;
const devBase = import.meta.env.VITE_DEVELOP_APP_BASE_URL;
const prodBase = import.meta.env.VITE_PRODUCTION_APP_BASE_URL || window.location.origin;
const NEW_TOKEN_HEADER = "x-new-token";
const AUTH_DIALOG_THROTTLE_MS = 1000;
let isHandlingAuthError = false;
let lastAuthDialogOpenedAt = 0;

export function generateWebApiBase() {
    const base: string = isDev ? devBase : prodBase;
    return `${base}${import.meta.env.VITE_APP_WEB_API_PREFIX || "/api"}`;
}

function readHeader(headers: unknown, name: string): string | undefined {
    const getter = (headers as { get?: (key: string) => unknown } | undefined)?.get;
    const raw = getter
        ? getter.call(headers, name)
        : ((headers as Record<string, unknown> | undefined)?.[name] ??
          (headers as Record<string, unknown> | undefined)?.[name.toLowerCase()]);

    if (Array.isArray(raw)) {
        return typeof raw[0] === "string" ? raw[0] : undefined;
    }

    return typeof raw === "string" ? raw : undefined;
}

function getFailedRequestToken(error: unknown): string | undefined {
    const maybeAxiosError = error as
        | {
              config?: { headers?: unknown };
              response?: { config?: { headers?: unknown } };
          }
        | undefined;
    const headers = maybeAxiosError?.config?.headers ?? maybeAxiosError?.response?.config?.headers;
    const authorization = readHeader(headers, "Authorization");
    const match = authorization?.match(/^Bearer\s+(.+)$/i);

    return match?.[1];
}

function isStaleAuthError(error: unknown): boolean {
    const currentToken = getPluginAccessToken();
    if (!currentToken) return false;

    const failedRequestToken = getFailedRequestToken(error);
    return !failedRequestToken || failedRequestToken !== currentToken;
}

function handleHttpError(error: HttpError): void {
    const message = error.message || "Bad request";
    toast.error(message);
}

async function handleAuthError(_error: unknown): Promise<void> {
    if (isStaleAuthError(_error)) {
        return;
    }

    if (isHandlingAuthError) {
        return;
    }
    isHandlingAuthError = true;

    try {
        await useAuthStore.getState().authActions.logout();

        if (location.pathname.includes("/login")) {
            return;
        }

        const now = Date.now();
        if (now - lastAuthDialogOpenedAt < AUTH_DIALOG_THROTTLE_MS) {
            return;
        }
        lastAuthDialogOpenedAt = now;
        openAuthDialog({ initialPage: AuthDialogPage.ACCOUNT_INPUT });
    } finally {
        isHandlingAuthError = false;
    }
}

async function handleAccessError(): Promise<void> {
    const userInfo = useAuthStore.getState().auth.userInfo;
    const target = hasConsoleAccess(userInfo)
        ? getFirstConsoleMenuPath(userInfo?.menus ?? [])
        : WEB_HOME_PATH;

    if (location.pathname !== target) {
        location.replace(target);
    }
}

async function handleAuthResponse(response: AxiosResponse): Promise<void> {
    const headers = response.headers as Record<string, unknown> & {
        get?: (name: string) => unknown;
    };
    const rawToken = headers.get?.(NEW_TOKEN_HEADER) ?? headers[NEW_TOKEN_HEADER];
    const newToken = Array.isArray(rawToken) ? rawToken[0] : rawToken;

    if (typeof newToken === "string" && newToken) {
        useAuthStore.getState().authActions.setToken(newToken);
    }

    const method = response.config.method?.toUpperCase();
    if (method && !["GET", "HEAD", "OPTIONS"].includes(method)) {
        notifyPowerBalanceChanged(`${method} ${response.config.url ?? ""}`);
    }
}

export const apiHttpClient = createHttpClient({
    baseURL: isDev ? devBase : prodBase,
    pathPrefix: import.meta.env.VITE_APP_WEB_API_PREFIX || "/api",
    parseResponse: createStandardApiParser(),
    hooks: {
        getAccessToken: async () => {
            return useAuthStore.getState().auth.token || "";
        },
        onResponse: handleAuthResponse,
        onAuthError: handleAuthError,
        onAccessError: handleAccessError,
        onError: handleHttpError,
    },
});

export const consoleHttpClient = createHttpClient({
    baseURL: isDev ? devBase : prodBase,
    pathPrefix: import.meta.env.VITE_APP_CONSOLE_API_PREFIX || "/console",
    parseResponse: createStandardApiParser(),
    hooks: {
        getAccessToken: async () => {
            return useAuthStore.getState().auth.token || "";
        },
        onResponse: handleAuthResponse,
        onAuthError: handleAuthError,
        onAccessError: handleAccessError,
        onError: handleHttpError,
    },
});

function extractPluginIdentifierFromPath(pathname?: string): string | undefined {
    const match = pathname?.match(/^\/(?:extension|apps)\/([^/]+)/);
    return match?.[1];
}

function readCookie(name: string): string {
    if (typeof document === "undefined") {
        return "";
    }

    const match = document.cookie.split("; ").find((item) => item.startsWith(`${name}=`));
    if (!match) {
        return "";
    }

    try {
        return decodeURIComponent(match.slice(name.length + 1));
    } catch {
        return "";
    }
}

let authBridgeToken: string | undefined;

function consumeAuthTokenFromUrl(): string {
    if (typeof window === "undefined") {
        return "";
    }

    const params = new URLSearchParams(window.location.search);
    const encoded = params.get("_t");
    if (!encoded) {
        return "";
    }

    let token = "";
    try {
        token = atob(encoded);
    } catch {
        token = "";
    }

    params.delete("_t");
    const cleanUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : "") +
        window.location.hash;
    window.history.replaceState({}, "", cleanUrl);

    if (token) {
        useAuthStore.getState().authActions.setToken(token);
    }

    return token;
}

function getAuthBridgeToken(): string {
    if (authBridgeToken !== undefined) {
        return authBridgeToken;
    }

    authBridgeToken = consumeAuthTokenFromUrl();
    return authBridgeToken;
}

function getPluginAccessToken(): string {
    if (typeof window === "undefined") {
        return "";
    }

    return (
        useAuthStore.getState().auth.token || readCookie("__user_token__") || getAuthBridgeToken()
    );
}

/**
 * Extract plugin identifier from the active URL or Vite extension base.
 */
function getPluginIdentifierFromUrl(): string {
    const identifier =
        extractPluginIdentifierFromPath(window.location.pathname) ||
        extractPluginIdentifierFromPath(import.meta.env.BASE_URL);

    if (!identifier) {
        throw new Error("Unable to extract plugin identifier from URL path");
    }

    return identifier;
}

async function handlePluginAuthError(_error: unknown): Promise<void> {
    if (isStaleAuthError(_error)) {
        return;
    }

    if (isHandlingAuthError) {
        return;
    }
    isHandlingAuthError = true;

    await useAuthStore.getState().authActions.logout();

    if (isDev) {
        location.replace(`${devBase}/login?redirect=${encodeURIComponent(location.href)}`);
    } else {
        location.replace(`/login?redirect=${location.pathname}`);
    }
}

/**
 * Create plugin-specific HTTP clients with plugin identifier in path prefix
 * @param pluginIdentifier - Plugin unique identifier, if not provided, will extract from URL
 * @returns Object containing apiHttpClient and consoleHttpClient for plugin
 */
export function createPluginHttpClients(pluginIdentifier?: string) {
    const identifier = pluginIdentifier || getPluginIdentifierFromUrl();

    const pluginApiHttpClient = createHttpClient({
        baseURL: isDev ? devBase : prodBase,
        pathPrefix: `/${identifier}${import.meta.env.VITE_APP_WEB_API_PREFIX || "/api"}`,
        parseResponse: createStandardApiParser(),
        hooks: {
            getAccessToken: async () => {
                return getPluginAccessToken();
            },
            onResponse: handleAuthResponse,
            onAuthError: handlePluginAuthError,
            onAccessError: handleAccessError,
            onError: handleHttpError,
        },
    });

    const pluginConsoleHttpClient = createHttpClient({
        baseURL: isDev ? devBase : prodBase,
        pathPrefix: `/${identifier}${import.meta.env.VITE_APP_CONSOLE_API_PREFIX || "/console"}`,
        parseResponse: createStandardApiParser(),
        hooks: {
            getAccessToken: async () => {
                return getPluginAccessToken();
            },
            onResponse: handleAuthResponse,
            onAuthError: handlePluginAuthError,
            onAccessError: handleAccessError,
            onError: handleHttpError,
        },
    });

    return {
        apiHttpClient: pluginApiHttpClient,
        consoleHttpClient: pluginConsoleHttpClient,
    };
}
